import { Card, Flex, theme as antdTheme, Typography } from 'antd'
import type { FC, KeyboardEvent } from 'react'
import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { PillList } from './components/PillList'
import { TicketProgress } from './components/TicketProgress'
import { getCardPills } from './filtering/cardIndex'
import type { CardView, PillCategories } from './types'

/** antd's `hoverable` swaps the border for a shadow that's barely visible on dark themes. */
const ClickableCard = styled(Card)<{
  $hoverBorderColor: string
  $transition: string
}>`
  cursor: pointer;
  transition:
    border-color ${({ $transition }) => $transition},
    background-color ${({ $transition }) => $transition};

  &:hover {
    border-color: ${({ $hoverBorderColor }) => $hoverBorderColor};
    background: ${({ theme }) => theme.colors.gray[700]};
  }

  /* Inset, since the virtual list clips anything drawn outside the card. */
  &:focus-visible {
    outline: 0.125rem solid ${({ $hoverBorderColor }) => $hoverBorderColor};
    outline-offset: -0.125rem;
  }
`

const TitleBlock = styled(Flex)`
  min-width: 0;
`

interface TicketCardProps {
  card: CardView
  categories: PillCategories
  onSelect: (cardId: string) => void
}

const RawTicketCard: FC<TicketCardProps> = ({ card, categories, onSelect }) => {
  const { token } = antdTheme.useToken()
  const latest = card.tickets[0]
  const earlierCount = card.tickets.length - 1

  const pills = useMemo(
    () =>
      getCardPills(card).filter(
        (pill) => !categories[pill.category]?.showOnlyOnModal
      ),
    [card, categories]
  )

  const handleSelect = useCallback(() => onSelect(card.id), [onSelect, card.id])
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      onSelect(card.id)
    },
    [onSelect, card.id]
  )

  return (
    <ClickableCard
      size='small'
      role='button'
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      $hoverBorderColor={token.colorPrimaryHover}
      $transition={token.motionDurationMid}>
      <Flex vertical gap='small'>
        <Flex justify='space-between' align='flex-start' gap='small'>
          <TitleBlock vertical>
            <Typography.Text strong ellipsis>
              {card.title}
            </Typography.Text>
            {card.subtitle && (
              <Typography.Text type='secondary'>
                {card.subtitle}
              </Typography.Text>
            )}
          </TitleBlock>
          {latest.progress && <TicketProgress progress={latest.progress} />}
        </Flex>

        <Typography.Text type='secondary' ellipsis>
          {[latest.title, latest.subtitle].filter(Boolean).join(' · ')}
        </Typography.Text>

        {pills.length > 0 && <PillList pills={pills} categories={categories} />}

        {earlierCount > 0 && (
          <Typography.Text type='secondary'>
            +{earlierCount} earlier
          </Typography.Text>
        )}
      </Flex>
    </ClickableCard>
  )
}

export const TicketCard = memo(RawTicketCard)
